import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

const BOX = 260; // displayed circular crop box
const OUT = 512; // exported square size

// Simple circular avatar crop (drag to reposition, no zoom).
export default function AvatarCropDialog({ open, imageSrc, onCancel, onConfirm }) {
  const { t } = useLang();
  const imgRef = useRef(null);
  const drag = useRef(null);
  const [nat, setNat] = useState(null);
  const [scale, setScale] = useState(1);
  const [off, setOff] = useState({ x: 0, y: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !imageSrc) return;
    setNat(null);
    const im = new Image();
    im.onload = () => {
      imgRef.current = im;
      const s = BOX / Math.min(im.naturalWidth, im.naturalHeight);
      setNat({ w: im.naturalWidth, h: im.naturalHeight });
      setScale(s);
      setOff({ x: (BOX - im.naturalWidth * s) / 2, y: (BOX - im.naturalHeight * s) / 2 });
    };
    im.src = imageSrc;
  }, [open, imageSrc]);

  const clamp = useCallback((o) => {
    if (!nat) return o;
    const dispW = nat.w * scale, dispH = nat.h * scale;
    return { x: Math.min(0, Math.max(BOX - dispW, o.x)), y: Math.min(0, Math.max(BOX - dispH, o.y)) };
  }, [nat, scale]);

  const onDown = (e) => {
    drag.current = { sx: e.clientX, sy: e.clientY, ox: off.x, oy: off.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onMove = (e) => {
    if (!drag.current) return;
    const d = drag.current;
    setOff(clamp({ x: d.ox + (e.clientX - d.sx), y: d.oy + (e.clientY - d.sy) }));
  };
  const onUp = () => { drag.current = null; };

  async function confirm() {
    if (!imgRef.current || !nat) return;
    setBusy(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = OUT; canvas.height = OUT;
      const ctx = canvas.getContext("2d");
      const srcSize = BOX / scale;
      ctx.drawImage(imgRef.current, -off.x / scale, -off.y / scale, srcSize, srcSize, 0, 0, OUT, OUT);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.9));
      const file = new File([blob], "avatar.jpg", { type: "image/jpeg" });
      await onConfirm(file);
    } finally { setBusy(false); }
  }

  const dispW = nat ? nat.w * scale : BOX;
  const dispH = nat ? nat.h * scale : BOX;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent data-testid="avatar-crop-dialog" className="max-w-sm">
        <DialogHeader><DialogTitle>{t("hc.cropThePhoto")}</DialogTitle></DialogHeader>
        <p className="text-xs text-slate-500 -mt-2">{t("hc.moveThePhotoToFit")}</p>
        <div className="flex justify-center py-2">
          <div
            className="relative overflow-hidden rounded-full border-4 border-findo-navy/20 cursor-grab active:cursor-grabbing select-none touch-none bg-slate-100"
            style={{ width: BOX, height: BOX }}
            onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={onUp}
            data-testid="avatar-crop-area">
            {nat && (
              <img src={imageSrc} alt="" draggable="false"
                style={{ position: "absolute", left: off.x, top: off.y, width: dispW, height: dispH, maxWidth: "none" }} />
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel} className="rounded-full" data-testid="avatar-crop-cancel">{t("hc.cancel")}</Button>
          <Button onClick={confirm} disabled={busy || !nat} className="rounded-full bg-findo-navy font-semibold" data-testid="avatar-crop-confirm">
            {busy ? <FindoSpinner size={16} /> : t("hc.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
